import { useEffect, useMemo, useState } from "react";
import config from "../../config/config.json";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Eye, EyeOff } from "lucide-react";

const { API_BASE_URL } = config;

const getAuthHeaders = () => {
  const authData = JSON.parse(localStorage.getItem("auth"));
  const token = authData?.token;

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const emptyForm = {
  id: 0,
  name: "",
  email: "",
  phone: "",
  password: "",
  role: "Sales",
  gender: "",
};

export default function SalesPersons() {
  const [sales, setSales] = useState([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [showPwd, setShowPwd] = useState(false);
  //const [showOldPwd, setShowOldPwd] = useState(false);

  const [showPasswordModal, setShowPasswordModal] = useState(null);
  //const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // ✅ Safe logged user parse
  const storedUser = localStorage.getItem("user");
  const loggedInUser = storedUser
    ? JSON.parse(storedUser)
    : { role: "Sales", email: "" };

  // ---------------- FETCH USERS ----------------
  const fetchSales = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/Getusers`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({}),
      });
      const data = await res.json();
//console.log(data);
      const mapped = data.map((u) => ({
        id: u.id,
        name: `${u.firstname} ${u.lastname}`,
        gender: u.gender,
        email: u.email,
        phone: u.mobile,
        role: u.roleId === 1 ? "Admin" : "Salesperson",
      }));

      setSales(mapped);
    } catch (err) {
      toast.error("Failed to fetch users");
    }
  };

  useEffect(() => {
    fetchSales();
  }, []);

  // ---------------- SEARCH ----------------
  const filteredSales = useMemo(() => {
    return sales.filter((p) =>
      `${p.name} ${p.email} ${p.phone}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [sales, search]);

  // ---------------- SAVE USER (CREATE/EDIT) ----------------
  const handleSave = async () => {
    if (!form.name || !form.email || !form.gender) {
      toast.error("Name, Email and Gender are required");
      return;
    }

    const [firstname, ...rest] = form.name.trim().split(" ");
    const lastname = rest.join(" ");

    const payload = {
      Id: form.id, // 0 = create, >0 = edit
      Firstname: firstname,
      Lastname: lastname,
      Gender: form.gender,
      Email: form.email,
      Mobile: form.phone,
      Username: form.email,
      Password: form.id === 0 ? form.password : form.password || undefined,
      RoleId: form.role === "Admin" ? 1 : 2,
      Isactive: 1,
      IsApproved: 1,
    };

    try {
      const res = await fetch(`${API_BASE_URL}/auth/SignUp`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const text = await res.text();
      if (!res.ok) throw new Error(text);

      toast.success(text);
      setShowForm(false);
      setForm(emptyForm);
      setEditing(null);
      fetchSales();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ---------------- PASSWORD RESET ----------------
 const handlePasswordReset = async () => {
  if (!newPassword) {
    toast.error("Enter new password");
    return;
  }

  const payload = {
    Targeteduser: showPasswordModal.id,   // 👈 important
    OldPassword: "",
    NewPassword: newPassword,
  };

  try {
    const res = await fetch(`${API_BASE_URL}/auth/Changepswd`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    const text = await res.text();
    if (!res.ok) throw new Error(text);

    toast.success(text);
    setShowPasswordModal(null);
    setNewPassword("");
  } catch (err) {
    toast.error(err.message);
  }
};


  return (
    <div className="p-3 md:p-6">
      <ToastContainer position="top-right" />

      {/* Search + Add */}
      <div className="flex flex-col gap-2 mb-4 md:flex-row md:justify-between md:items-center">
        <input
          placeholder="Search..."
          className="border p-2 rounded w-full md:w-60"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          onClick={() => {
            setEditing(null);
            setForm(emptyForm);
            setShowForm(true);
          }}
          className="bg-blue-600 text-white py-2 px-2 rounded md:ml-2"
        >
          Add User
        </button>
      </div>

      {/* ---------- MOBILE CARDS ---------- */}
      <div className="space-y-3 md:hidden">
        {filteredSales.map((p) => (
          <div key={p.id} className="border p-3 rounded shadow bg-white">
            <p className="font-semibold">{p.name}</p>
            <p className="text-sm">{p.email}</p>
            <p className="text-sm">{p.phone}</p>
            <p className="text-xs bg-blue-100 inline-block px-2 py-1 rounded">
              {p.role}
            </p>

            <div className="flex gap-2 mt-3">
              <button
                onClick={() => {
                  setEditing(p);
                  setForm({
                    id: p.id,
                    name: p.name,
                    email: p.email,
                    phone: p.phone,
                    password: "",
                    role: p.role,
                    gender: p.gender || "",
                  });
                  setShowForm(true);
                }}
                className="flex-1 bg-indigo-600 text-white py-2 rounded"
              >
                Edit
              </button>
              <button
                onClick={() => setShowPasswordModal(p)}
                className="flex-1 bg-yellow-600 text-white py-2 rounded"
              >
                Reset
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ---------- DESKTOP TABLE ---------- */}
      <div className="hidden md:block overflow-auto">
        <table className="w-full border text-sm text-center">
          <thead className="bg-gray-100">
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSales.map((p) => (
              <tr key={p.id} className="border-t">
                <td>{p.name}</td>
                <td>{p.email}</td>
                <td>{p.phone}</td>
                <td>{p.role}</td>
                <td>
                  <button
                    onClick={() => {
                      setEditing(p);
                      setForm({
                        id: p.id,
                        name: p.name,
                        email: p.email,
                        phone: p.phone,
                        password: "",
                        role: p.role,
                        gender: p.gender || "",
                      });
                      setShowForm(true);
                    }}
                    className="bg-indigo-600 text-white px-2 py-1 m-1"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setShowPasswordModal(p)}
                    className="bg-yellow-600 text-white px-2 py-1 m-1"
                  >
                    Reset
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ---------- CREATE / EDIT MODAL ---------- */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
          <div className="bg-white p-5 rounded w-full max-w-md relative">
            <button
              onClick={() => setShowForm(false)}
              className="absolute top-2 right-3 text-xl font-bold text-red-500"
            >
              ✕
            </button>

            <h2 className="text-lg font-semibold mb-3">
              {form.id > 0 ? "Edit User" : "Create User"}
            </h2>

            <input
              placeholder="Full Name"
              className="border p-2 w-full mb-2 rounded"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
              placeholder="Email"
              className="border p-2 w-full mb-2 rounded"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <input
              placeholder="Phone"
              className="border p-2 w-full mb-2 rounded"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />

            <select
              className="border p-2 w-full mb-2 rounded"
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>

            {!editing && (
              <div className="relative mb-2">
                <input
                  type={showPwd ? "text" : "password"}
                  placeholder="Password"
                  className="border p-2 w-full rounded pr-10"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-2 top-2"
                >
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            )}

            <select
              className="border p-2 w-full mb-2 rounded"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              <option value="Admin">Admin</option>
              <option value="Sales">Sales</option>
            </select>

            <button
              onClick={handleSave}
              className="bg-blue-600 text-white w-full py-2 rounded"
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* ---------- PASSWORD MODAL ---------- */}
      {showPasswordModal && (
  <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
    <div className="bg-white p-5 rounded w-full max-w-sm relative">
      <button
        onClick={() => setShowPasswordModal(null)}
        className="absolute top-2 right-3 text-xl font-bold text-red-500"
      >
        ✕
      </button>

      <h2 className="text-lg font-semibold mb-3">Change Password</h2>

      <div className="relative mb-2">
        <input
          type={showPwd ? "text" : "password"}
          placeholder="New Password"
          className="border p-2 w-full rounded pr-10"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <button
          type="button"
          onClick={() => setShowPwd(!showPwd)}
          className="absolute right-2 top-2"
        >
          {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>

      <button
        onClick={handlePasswordReset}
        className="bg-green-600 text-white w-full py-2 rounded"
      >
        Update Password
      </button>
    </div>
  </div>
)}

    </div>
  );
}